import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Modal} from "../../../../../components/modal/modal.jsx";
import {RoleContext} from "../context/role-context.jsx";
import roleService from "../../../../../services/web/role-service.js";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import PermissionFormatter from "./role-permission-formatter.jsx";
import DependencyFormatter from "./role-dependency-formatter.jsx";
import {PermissionEnum} from "../../../../../utils/enums/permission-enum.js";
import Button from "../../../../../components/button/button.jsx";
import roleUtils from "../utils/role-utils.js";
import ConditionalDependencies from "../resources/conditional-dependencies.js";
import projectUtils from "../../../../../utils/project-utils.js";
import DependenciesList from "../resources/dependencies-list.js";
import DependenciesAllLanes from "../resources/dependencies-all-lanes.js";
import RoleModalDependencies from "./role-modal-dependencies.jsx";
import Alert from "../../../../../components/alert/alert.jsx";
import {FaTriangleExclamation} from "react-icons/fa6";
import RoleModalLanes from "./role-modal-lanes.jsx";
import RoleModalNoc from "./role-modal-noc.jsx";
import RoleModalPrograms from "./role-modal-programs.jsx";

const filterParams = {
    textFormatter: (r) => {
        if (r == null) return null;
        return r
            .toLowerCase()
            .replace(/[àáâãäå]/g, "a")
            .replace(/æ/g, "ae")
            .replace(/ç/g, "c")
            .replace(/[èéêë]/g, "e")
            .replace(/[ìíîï]/g, "i")
            .replace(/ñ/g, "n")
            .replace(/[òóôõö]/g, "o")
            .replace(/œ/g, "oe")
            .replace(/[ùúûü]/g, "u")
            .replace(/[ýÿ]/g, "y");
    }, debounceMs: 200, maxNumConditions: 1,
}

let selectionWasMadeDirectly = false;
let newList = [];

const RolePermissionModal = ({permissions, open, setOpen}) => {
    const {permissionsSelected, setPermissionsSelected, maintenanceSelected,setMaintenanceSelected, lanesSelected, setLanesSelected, permissionsWithDependenciesNotChecked, setPermissionsWithDependenciesNotChecked, lanesRef, maintenancesRef, activeUsers, protectionProgramSelected, setProtectionProgramSelected, protectionProgramRef} = useContext(RoleContext);

    const [modalNoc, openModalNoc] = useState(false);
    const [modalDependencies, openModalDependencies] = useState(false);
    const [modalLanes, openModalLanes] = useState(false);
    const [modalProgram, setModalProgram] = useState(false);
    const [permissionsDependency, setPermissionsDependency] = useState([]);
    const [conditionalPermissionsDependency, setConditionalPermissionsDependency] = useState([]);
    const [rowPermission, setRowPermission] = useState(null);
    const [listPermissionsLanes, setListPermissionsLanes] = useState([])

    const [columnDefs, setColumnDefs] = useState([]);

    useEffect(() => {
        if (!permissions) return;

        const permissionOrder = new Map(permissions.map(({name}, index) => [name, index]));
        const getPermissionPriority = ({group, name, text}) => {
            if (group === "Relatório") {
                if (text.startsWith("Abordagem -")) return 1;
                if (text.startsWith("Atendimento e despacho - Atendimentos -")) return 2;
                if (text.startsWith("Atendimento e despacho - Guarnições -")) return 3;
                if (text.startsWith("Atendimento e despacho - Despachos -") || text.startsWith("Atendimento e despacho - CAD -")) return 4;
                if (name.startsWith("sentry.report.dispatch.bo.") || name.startsWith("sentry.report.bo.") || name === "sentry.report.dispatch.responsible.bogcm") return 5;
                if (text.startsWith("Formulários -")) return 6;
                if (text.startsWith("Gestão -")) return 7;
                if (text.startsWith("Inteligência -")) return 8;
                if (text.startsWith("Monitoramento patrimonial -")) return 9;
                if (text.startsWith("Pontos de coleta -")) return 10;
                if (text.startsWith("SOS Cidadão -")) return 12;

                return 11;
            }

            if (group === "Atendimento e despacho") {
                if (text === "Acesso ao módulo") return 1;
                if (text.startsWith("Configuração -")) return 2;
                if (text.startsWith("Atendimentos -")) return 3;
                if (text.startsWith("Tarefas -")) return 4;
                if (text.startsWith("Guarnições -")) return 5;
                if (text.startsWith("Despachos -")) return 6;
                if (text.startsWith("Diário de plantão -") || text.endsWith("do plantão")) return 7;
                if (name.startsWith("sentry.bogcm.")) return 8;
            }

            return Number.MAX_SAFE_INTEGER;
        };

        setColumnDefs([
            {
                headerName: "Grupo",
                field: "group",
                width: 190,
                filterParams,
                comparator: (firstGroup, secondGroup, firstNode, secondNode, isDescending) => {
                    const groupDifference = firstGroup.localeCompare(secondGroup, "pt-BR");
                    if (groupDifference) return groupDifference;

                    const permissionDifference = getPermissionPriority(firstNode.data) - getPermissionPriority(secondNode.data);
                    if (permissionDifference) return isDescending ? -permissionDifference : permissionDifference;

                    const orderDifference = permissionOrder.get(firstNode.data.name) - permissionOrder.get(secondNode.data.name);
                    return isDescending ? -orderDifference : orderDifference;
                }
            },
            {
                headerName: "Permissão",
                field: "text",
                cellRenderer: PermissionFormatter,
                flex: 1,
                filterParams,
                cellRendererParams: {openModalLanes, openModalNoc, setModalProgram}
            },
            {
                headerName: "Dependências",
                field: "",
                filter: false, sortable: false,
                width: 125,
                cellClass: 'dependency-column',
                cellRenderer: DependencyFormatter,
                cellRendererParams: {
                    permissions,
                    openModalDependencies,
                    setPermissionsDependency,
                    setRowPermission,
                    setConditionalPermissionsDependency
                }
            }
        ])
    }, [permissions]);

    useEffect(() => {
        if (permissionsWithDependenciesNotChecked.length === 0) {
            newList = [];
        }
    }, [permissionsWithDependenciesNotChecked]);

    const gridRef = useRef(undefined);

    const onGridReady = ({api}) => {
        if (api) {
            const selectedPermissions = new Set(permissionsSelected.map(p => p.name));

            const isSosManageSelected = selectedPermissions.has(PermissionEnum.SOS_MANAGE_CITIZEN);
            const isSosViewSelected = selectedPermissions.has(PermissionEnum.SOS_VIEW);

            const isSosProgramSelected = selectedPermissions.has(PermissionEnum.SOS_MANAGE_PROGRAM);
            const isSosAllProgramSelected = selectedPermissions.has(PermissionEnum.SOS_MANAGE_ALL_PROGRAM);

            const isEncounterAppUnrestrictedSelected = selectedPermissions.has(PermissionEnum.ENCOUNTER_APP);
            const isEncounterDispatchAppOnlySelected = selectedPermissions.has(PermissionEnum.ENCOUNTER_APP_DISPATCH);
            const hasBothEncounterPermissions = isEncounterAppUnrestrictedSelected && isEncounterDispatchAppOnlySelected;

            api.forEachNode(row => {
                const {name} = row.data;

                const isManage = name === PermissionEnum.SOS_MANAGE_CITIZEN;
                const isView = name === PermissionEnum.SOS_VIEW;

                const program = name === PermissionEnum.SOS_MANAGE_PROGRAM;
                const allProgram = name === PermissionEnum.SOS_MANAGE_ALL_PROGRAM;

                const isEncounterAppUnrestricted = name === PermissionEnum.ENCOUNTER_APP;
                const isEncounterDispatchAppOnly = name === PermissionEnum.ENCOUNTER_APP_DISPATCH;

                let selected = permissionsSelected.some(permission => permission.name === name);

                if (hasBothEncounterPermissions) {
                    if (isEncounterAppUnrestricted) {
                        row.data.disabled = false;
                        selected = true;
                    }

                    if (isEncounterDispatchAppOnly) {
                        row.data.disabeld = true;
                        selected = false;
                    }
                } else if (
                    (isSosViewSelected && isManage) ||
                    (isSosManageSelected && isView) ||
                    (isSosProgramSelected && allProgram) ||
                    (isSosAllProgramSelected && program) ||
                    (isEncounterAppUnrestrictedSelected && isEncounterDispatchAppOnly) ||
                    (isEncounterDispatchAppOnlySelected && isEncounterAppUnrestricted)
                ) {
                    row.data.disabled = true;
                    selected = false
                } else {
                    row.data.disabled = false;
                }

                row.setSelected(selected);
            });
        }
    }

    const defaultColDef = useMemo(() => {
        return {
            filter: true,
            floatingFilter: true,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            suppressHeaderContextMenu: true,
            suppressFloatingFilterButton: true,
        };
    }, []);

    const rowSelection = useMemo(() => {
        return {
            mode: "multiRow",
            headerCheckbox: false,
            enableSelectionWithoutKeys: true,
            enableClickSelection: false,
        };
    }, []);

    const onRowSelected = async (row) => {
        await handleSelectedPermission(row);
    }

    const handleSelectedPermission = async (row) => {
        if (row.event) {
            selectionWasMadeDirectly = true;
        }

        const permissionSelected = row.data.name;
        const isSelected = row.node.isSelected();
        newList = [...permissionsWithDependenciesNotChecked];

        await handleDispatchDependencies(row, isSelected);
        await handleConditionalDependencies(permissionSelected, newList, isSelected);
        await handleDependencies(permissionSelected, newList, isSelected, row);

        setPermissionsWithDependenciesNotChecked([...newList]);
        selectionWasMadeDirectly = false;
    }

    const handleConditionalDependencies = async (permissionSelected, newList, isSelected) => {
        const inverseConditionalDependencies = await roleUtils.getDependencies(permissionSelected, true, permissions, ConditionalDependencies);
        const conditionalDependencies = await roleUtils.getDependencies(permissionSelected, false, permissions, ConditionalDependencies);
        if (isSelected) {
            if (conditionalDependencies.length > 0) {
                let conditionalDependencyChecked = false;
                for (const dependency of conditionalDependencies) {
                    const isChecked = await checkSelected(dependency.name);
                    if (isChecked) {
                        conditionalDependencyChecked = true;
                        break;
                    }
                }

                if (!conditionalDependencyChecked) {
                    newList.push(permissionSelected);
                }
            }

            for (const inverseDependency of inverseConditionalDependencies) {
                const {name} = inverseDependency;
                if (permissionsWithDependenciesNotChecked.includes(name)) {
                    await removeItemFromArray(newList, name);
                }
            }
        } else {
            for (const inverseDependency of inverseConditionalDependencies) {
                const {name} = inverseDependency;
                const isChecked = await checkSelected(name);
                const conditionalDependencies = await roleUtils.getDependencies(name, false, permissions, ConditionalDependencies);
                if (conditionalDependencies.length > 0) {
                    let hasAnotherConditionalChecked = false;
                    for (const dependency of conditionalDependencies) {
                        if (dependency.name !== permissionSelected) {
                            const isChecked = await checkSelected(dependency.name);
                            if (isChecked) {
                                hasAnotherConditionalChecked = true;
                                break;
                            }
                        }
                    }

                    if (!hasAnotherConditionalChecked && !permissionsWithDependenciesNotChecked.includes(name) && isChecked) {
                        newList.push(name);
                    }
                } else {
                    if (isChecked && !permissionsWithDependenciesNotChecked.includes(name)) {
                        newList.push(name);
                    }
                }
            }
        }
    }

    // Quando o usuário for responsável por uma guarnição ativa e possuir a permissão PermissionEnum.DISPATCH_GARRISON_LEADER
    const handleDispatchDependencies = async (row, isSelected) => {
        if (row.data.name === PermissionEnum.DISPATCH_GARRISON_LEADER) {
            const permissionAlreadySaved = permissionsSelected.filter(item => item.text === PermissionEnum.DISPATCH_GARRISON_LEADER);

            if (permissionAlreadySaved && !isSelected) {
                const activeUsersId = activeUsers.map(item => item.id);
                roleService.dispatchDependencies({ permissionId: row.data.id, users: activeUsersId })
                    .then((data) => {
                        if (data.length) {
                            row.node.setSelected(true);
                            projectUtils.swalErrorMessage(
                                'Erro',
                                "Usuário é responsável por uma guarnição ativa. Não é possível remover essa permissão.",
                                'Ok'
                            );
                        }
                    });
            }
        }
    }

    const handleDependencies = async (permissionSelected, newList, isSelected, row) => {
        const inverseDependencies = await roleUtils.getDependencies(permissionSelected, true, permissions, DependenciesList);
        if (isSelected) {
            const dependencies = await roleUtils.getDependencies(permissionSelected, false, permissions, DependenciesList);
            for (const dependency of dependencies) {
                const isChecked = await checkSelected(dependency.name);
                if (!isChecked && !permissionsWithDependenciesNotChecked.includes(dependency.name)) {
                    if (!newList.includes(permissionSelected)) {
                        newList.push(permissionSelected);
                    }
                    break;
                }
            }

            for (const inverseDependency of inverseDependencies) {
                const {name} = inverseDependency;

                if (permissionsWithDependenciesNotChecked.includes(name)) {
                    let hasUncheck = false;
                    const dependencies = await roleUtils.getDependencies(name, false, permissions, DependenciesList);

                    for (const dependency of dependencies) {
                        const isChecked = await checkSelected(dependency.name);
                        if (!isChecked) {
                            hasUncheck = true;
                            break;
                        }
                    }

                    if (!hasUncheck) {
                        await removeItemFromArray(newList, name);
                    }
                }
            }

            if (selectionWasMadeDirectly) {
                if (permissionSelected === PermissionEnum.CAPTURE_PLACE_NOC) openModalNoc(true);
                if (permissionSelected === PermissionEnum.PASSAGE_RESTRICTED_LANE_ACCESS) openModalLanes(true);
                if (permissionSelected === PermissionEnum.SOS_MANAGE_PROGRAM) setModalProgram(true);
            }

            if (DependenciesAllLanes.includes(permissionSelected)) {
                setListPermissionsLanes((prevItems) => [...prevItems, permissionSelected]);
            }

            if ([PermissionEnum.SOS_MANAGE_CITIZEN, PermissionEnum.SOS_VIEW, PermissionEnum.SOS_MANAGE_ALL_PROGRAM, PermissionEnum.SOS_MANAGE_PROGRAM].includes(permissionSelected)) {
                await handleSosPermissions(permissionSelected);
            }

            if ([PermissionEnum.ENCOUNTER_APP, PermissionEnum.ENCOUNTER_APP_DISPATCH].includes(permissionSelected)) {
                handleEncounterPermissions(permissionSelected);
            }

            setRowPermission(row);
        } else {
            await removeItemFromArray(newList, permissionSelected);

            for (const inverseDependency of inverseDependencies) {
                const isChecked = await checkSelected(inverseDependency.name);
                if (isChecked && !permissionsWithDependenciesNotChecked.includes(inverseDependency.name)) {
                    newList.push(inverseDependency.name);
                }
            }

            if (permissionSelected === PermissionEnum.SOS_MANAGE_PROGRAM) {
                document.querySelectorAll(`[row-id^=all_program]`)?.forEach(el => el.classList.remove('row-disabled'));
            }

            if (permissionSelected === PermissionEnum.SOS_MANAGE_ALL_PROGRAM) {
                document.querySelectorAll(`[row-id^=program]`)?.forEach(el => el.classList.remove('row-disabled'));
            }

            if (permissionSelected === PermissionEnum.SOS_VIEW) {
                document.querySelectorAll(`[row-id^=manage_]`)?.forEach(el => el.classList.remove('row-disabled'));
            }

            if (permissionSelected === PermissionEnum.ENCOUNTER_APP) {
                document.querySelector(`[row-id^=encounter_app_dispatch_only]`)?.classList.remove('row-disabled');
            }

            if (permissionSelected === PermissionEnum.ENCOUNTER_APP_DISPATCH) {
                document.querySelector(`[row-id^=encounter_app_unrestricted]`)?.classList.remove('row-disabled');
            }

            if (permissionSelected === PermissionEnum.SOS_MANAGE_CITIZEN) {
                const manageNode = [];
                gridRef.current?.api.forEachNode(node => {
                    if (node.data.name === PermissionEnum.SOS_MANAGE_CITIZEN) manageNode.push(node);
                });

                if (manageNode.every(node => !node.selected)) {
                    document.querySelector(`[row-id^=sos_view]`)?.classList.remove('row-disabled');
                }
            }

            if (permissionSelected === PermissionEnum.CAPTURE_PLACE_NOC) setMaintenanceSelected(null)
            if (permissionSelected === PermissionEnum.PASSAGE_RESTRICTED_LANE_ACCESS) setLanesSelected([])
            if (permissionSelected === PermissionEnum.SOS_MANAGE_PROGRAM) setProtectionProgramSelected([])
            if (DependenciesAllLanes.includes(permissionSelected)) {
                setListPermissionsLanes((prevItems) => prevItems.filter((item) => item !== permissionSelected));
            }

            setRowPermission(null);
        }
    }

    const handleSosPermissions = (permissionSelected) => {
        const sosPermission = (p1, p2, id1, id2, node) => {
            const {name} = node.data;

            if (p1 === permissionSelected && name === p2) {
                document.querySelector(`[row-id^=${id1}]`)?.classList.remove('row-disabled');
                document.querySelector(`[row-id^=${id2}]`)?.classList.add('row-disabled');

                node.setSelected(false);
            }

            if (permissionSelected === p2 && p1 === name) {
                document.querySelector(`[row-id^=${id1}]`)?.classList.add('row-disabled');
                document.querySelector(`[row-id^=${id2}]`)?.classList.remove('row-disabled');

                node.setSelected(false);
            }
        }

        gridRef.current?.api.forEachNode(node => {
            sosPermission(PermissionEnum.SOS_MANAGE_CITIZEN, PermissionEnum.SOS_VIEW, "manage_citizen", "sos_view", node);
            sosPermission(PermissionEnum.SOS_MANAGE_ALL_PROGRAM, PermissionEnum.SOS_MANAGE_PROGRAM, "all_program", "program", node);
        });
    }

    const handleEncounterPermissions = (permissionSelected) => {
        const encounterPermissions = (p1, p2, id1, id2, node) => {
            const { name } = node.data;

            if (p1 === permissionSelected && name === p2) {
                document.querySelector(`[row-id^=${id1}]`)?.classList.remove('row-disabled');
                document.querySelector(`[row-id^=${id2}]`)?.classList.add('row-disabled');

                node.setSelected(false);
            }

            if (permissionSelected === p2 && p1 === name) {
                document.querySelector(`[row-id^=${id1}]`)?.classList.add('row-disabled');
                document.querySelector(`[row-id^=${id2}]`)?.classList.remove('row-disabled');

                node.setSelected(false);
            }
        }

        gridRef.current?.api.forEachNode(node => {
            encounterPermissions(
                PermissionEnum.ENCOUNTER_APP,
                PermissionEnum.ENCOUNTER_APP_DISPATCH,
                "encounter_app_unrestricted",
                "encounter_app_dispatch_only",
                node
            );
        });
    }

    const removeItemFromArray = async (array, item) => {
        for (let j = 0; j < array.length; j++) {
            if (array[j] === item) {
                array.splice(j, 1);
            }
        }
    }

    const checkSelected = async (name) => {
        let isSelected = false;
        gridRef.current?.api.forEachNode(row => {
            if (row.data.name === name) {
                isSelected = row.isSelected();
            }
        });

        return isSelected;
    }

    const resetLaneSelected = () => {
        setLanesSelected([]);
    }

    const closeModal = () => {
        setOpen(false);

        setLanesSelected(lanesRef.current ?? []);
        setMaintenanceSelected(maintenancesRef.current);
        setProtectionProgramSelected(protectionProgramRef.current);
        setPermissionsWithDependenciesNotChecked([]);
    }

    const lanesAlert = !!listPermissionsLanes.length;
    const dependenciesAlert = !!permissionsWithDependenciesNotChecked.length;
    const dependenciesLength = permissionsWithDependenciesNotChecked.length;

    const savePermissions = () => {
        if (permissionsWithDependenciesNotChecked.length > 0) return;

        const getPermissionList = () => {
            const permissionsSelectedList = [];
            gridRef.current.api.forEachNode(row => {
                if (row.isSelected()) {
                    permissionsSelectedList.push(row.data)
                }
            });

            return permissionsSelectedList;
        }

        const permissionsSelectedList = getPermissionList();
        if (lanesSelected.length === 0) {
            const permissionFind = permissionsSelectedList.find(p => p.name === PermissionEnum.PASSAGE_RESTRICTED_LANE_ACCESS);
            if (permissionFind) {
                projectUtils.swalErrorMessage('', "Selecione câmeras de OCR.", 'Ok');
                return;
            }
        }

        if (protectionProgramSelected.length === 0) {
            const permissionFind = permissionsSelectedList.find(p => p.name === PermissionEnum.SOS_MANAGE_PROGRAM);
            if (permissionFind) {
                projectUtils.swalErrorMessage('', "Selecione programa de proteção.", 'Ok');
                return;
            }
        }

        maintenancesRef.current = maintenanceSelected;
        lanesRef.current = lanesSelected;
        setPermissionsSelected(permissionsSelectedList);
        setOpen(false);
    }

    return (
        <>
            <Modal open={open} setOpen={closeModal} title={"Editar permissões"} isCloseClick={false}>
                <Modal.Body>
                    <div style={{height: (lanesAlert && dependenciesAlert) ? "calc(100vh - 278px)" : "calc(100vh - 240px)"}}>
                        <AgGridReact
                            ref={gridRef}
                            rowData={permissions ?? []}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            rowSelection={rowSelection}
                            animateRows={false}
                            suppressCellFocus={true}
                            gridOptions={{localeText: aggridUtils.localization()}}
                            suppressColumnVirtualisation={true}
                            onGridReady={onGridReady}
                            theme={aggridUtils.generalTheme()}
                            getRowClass={params => params.data.disabled ? "row-disabled" : ""}
                            onRowSelected={onRowSelected}
                            getRowId={({data}) => {
                                switch (data.name) {
                                    case PermissionEnum.SOS_MANAGE_PROGRAM: return "program"
                                    case PermissionEnum.SOS_MANAGE_ALL_PROGRAM: return "all_program"
                                    case PermissionEnum.SOS_MANAGE_CITIZEN: return "manage_citizen"
                                    case PermissionEnum.SOS_VIEW: return "sos_view"
                                    case PermissionEnum.ENCOUNTER_APP: return "encounter_app_unrestricted"
                                    case PermissionEnum.ENCOUNTER_APP_DISPATCH: return "encounter_app_dispatch_only"
                                    default: return data.id;
                                }
                            }}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer className={"!justify-between items-center"}>
                    <div className={"flex gap-2 flex-col"}>
                        {
                            lanesAlert &&
                            <Alert className={"flex gap-2 items-center"} target={"warning"}>
                                <FaTriangleExclamation /> Atenção, há permissões selecionadas que possibilitam a visualização de dados relacionados a todas as câmeras.
                            </Alert>
                        }
                        {
                            dependenciesAlert &&
                            <Alert className={"flex gap-2 items-center"} target={"warning"}>
                                <FaTriangleExclamation /> {dependenciesLength} {dependenciesLength > 1 ? "permissões" : "permissão"} com dependências a resolver.
                            </Alert>
                        }
                    </div>

                    <div className={"flex gap-2"}>
                        <Button onClick={savePermissions} disabled={permissionsWithDependenciesNotChecked.length > 0}>
                            Confirmar
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>

            <RoleModalDependencies
                open={modalDependencies}
                setOpen={openModalDependencies}
                permissionsDependency={permissionsDependency}
                conditionalPermissionsDependency={conditionalPermissionsDependency}
                tablePermissions={gridRef}
                rowPermission={rowPermission}
                resetLaneValue={resetLaneSelected}
            />

            <RoleModalLanes
                open={modalLanes}
                setOpen={openModalLanes}
                rowPermission={rowPermission}
            />

            <RoleModalNoc
                open={modalNoc}
                setOpen={openModalNoc}
                rowPermission={rowPermission}
                permissions={permissions}
            />

            <RoleModalPrograms
                open={modalProgram}
                setOpen={setModalProgram}
                rowPermission={rowPermission}
            />
        </>
    );
};

export default RolePermissionModal;
